import { useEffect, useMemo, useState } from "react"
import { UtilityCompanyCard, UtilityUnlinkedBillsCard } from "./UtilityCompanyCard"
import { COMPANY_CATEGORIES, formatKsh, getCompanyCategory } from "./utilityAdminDisplay"

const COMPANY_PAGE_SIZE = 10

function CategoryPicker({ category, counts, onPick }) {
  return (
    <div className="flex flex-wrap gap-1 px-3 pt-3" role="group" aria-label="Biller type">
      {COMPANY_CATEGORIES.map((item) => {
        const active = item.key === category
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={active}
            onClick={() => onPick(item.key)}
            className={`rounded-full border px-2 py-1 text-[11px] font-semibold transition-colors ${
              active
                ? "border-emerald-600 bg-emerald-600 text-white"
                : "border-gray-200 bg-white text-gray-600 hover:border-emerald-300 hover:text-emerald-700"
            }`}
          >
            {item.label}
            <span className={`ml-1 font-normal ${active ? "text-emerald-100" : "text-gray-400"}`}>{counts[item.key]}</span>
          </button>
        )
      })}
    </div>
  )
}

// Companies are already filtered by the search. Each category button shows how
// many of them are in that list, so a search match in another list is visible.
function UtilityCompanyLists({ companies, category, onCategoryChange, unlinked, summary = {}, search, onImpersonate }) {
  const [showCount, setShowCount] = useState(COMPANY_PAGE_SIZE)
  const searchActive = Boolean(search.trim())

  useEffect(() => {
    setShowCount(COMPANY_PAGE_SIZE)
  }, [search, category])

  const byCategory = useMemo(() => {
    const lists = Object.fromEntries(COMPANY_CATEGORIES.map((item) => [item.key, []]))
    companies.forEach((company) => lists[getCompanyCategory(company)].push(company))
    return lists
  }, [companies])

  const activeCategory = COMPANY_CATEGORIES.find((item) => item.key === category) || COMPANY_CATEGORIES[0]
  const list = byCategory[activeCategory.key]
  const counts = Object.fromEntries(COMPANY_CATEGORIES.map((item) => [item.key, byCategory[item.key].length]))
  const isRentals = activeCategory.key === "rentals"
  const showUnlinked = isRentals && Boolean(unlinked) && !searchActive

  return (
    <div>
      <CategoryPicker category={activeCategory.key} counts={counts} onPick={onCategoryChange} />
      {isRentals && unlinked && summary.owedNotLinked > 0 && !searchActive && (
        <p className="px-3 pt-3 text-[11px] leading-snug text-gray-500">
          Owed <span className="font-semibold text-gray-800">{formatKsh(summary.owed)}</span> = companies{" "}
          {formatKsh(summary.owedByCompanies)} + bills with no billing company {formatKsh(summary.owedNotLinked)}
        </p>
      )}
      {list.length === 0 && !showUnlinked ? (
        <p className="p-4 text-xs text-gray-400">{searchActive ? "No matches in this list." : activeCategory.empty}</p>
      ) : (
        <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
          {list.slice(0, showCount).map((company) => (
            <UtilityCompanyCard key={company.id} company={company} onImpersonate={onImpersonate} />
          ))}
          {list.length > showCount && (
            <button
              type="button"
              onClick={() => setShowCount((count) => count + COMPANY_PAGE_SIZE)}
              className="w-full px-3 py-2.5 text-left text-xs font-medium text-emerald-700 hover:bg-emerald-50/40"
            >
              Show more companies ({list.length - showCount})
            </button>
          )}
          {showUnlinked && <UtilityUnlinkedBillsCard unlinked={unlinked} onImpersonate={onImpersonate} />}
        </div>
      )}
    </div>
  )
}

export default UtilityCompanyLists
